import React, { useState } from "react";
import axios from "axios";
import { Logo } from "./Logo";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_clarity-to-glow/artifacts/z87ow7t4_AGPT%20Ads_Stories_20260424_155644_0000.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SignUp = ({ onComplete }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Returning-user lookup state
  const [showResume, setShowResume] = useState(false);
  const [resumeEmail, setResumeEmail] = useState("");
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!firstName.trim() || !email.trim()) {
      setError("Please fill in both your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/challenge/signup`, {
        first_name: firstName.trim(),
        email: email.trim(),
      });
      onComplete(data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please check your email and try again.";
      setError(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (e) => {
    e.preventDefault();
    setResumeError("");
    if (!resumeEmail.trim()) {
      setResumeError("Please enter the email you signed up with.");
      return;
    }
    setResumeLoading(true);
    try {
      const { data } = await axios.get(`${API}/challenge/user`, {
        params: { email: resumeEmail.trim() },
      });
      onComplete(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setResumeError(
          "We couldn't find that email. Sign up above to start your glow up."
        );
      } else {
        setResumeError("Something went wrong. Please try again.");
      }
    } finally {
      setResumeLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col md:flex-row w-full bg-cream animate-glow-fade-up"
      data-testid="signup-page"
    >
      {/* Logo top-left */}
      <div className="absolute top-6 left-6 md:top-10 md:left-12 z-20">
        <Logo />
      </div>

      {/* Hero image (mobile: top; desktop: right) */}
      <div
        className="relative w-full md:w-1/2 md:order-2 bg-espresso overflow-hidden"
        style={{ minHeight: "46vh" }}
        data-testid="hero-image-section"
      >
        <img
          src={HERO_IMAGE}
          alt="The woman you're becoming"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 18%" }}
          loading="eager"
        />
        {/* Subtle gold edge */}
        <div className="absolute inset-y-0 left-0 w-[2px] bg-gold hidden md:block" />
        {/* Overlaid header tag for poster feel */}
        <div className="absolute bottom-8 left-8 right-8 md:bottom-12 md:left-12 md:right-12">
          <div className="inline-block bg-cream/90 backdrop-blur-sm px-5 py-3">
            <p
              className="font-body uppercase text-[10px] md:text-xs tracking-[0.32em] text-espresso"
              data-testid="hero-ribbon"
            >
              The 5-Day Glow Up Challenge
            </p>
          </div>
        </div>
      </div>

      {/* Content side */}
      <div className="relative w-full md:w-1/2 md:order-1 flex flex-col justify-center px-6 pt-28 pb-16 sm:pt-32 sm:px-10 md:px-16 md:py-16 lg:px-24">
        <div className="max-w-[500px] mx-auto w-full flex flex-col gap-8">
          <span
            className="font-body uppercase text-xs tracking-[0.32em] text-gold"
            data-testid="top-label"
          >
            Free 5-Day Experience
          </span>

          <h1
            className="font-serif-editorial text-espresso leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
            data-testid="signup-headline"
          >
            The 5-Day
            <br />
            Glow Up
            <br />
            <span className="italic text-gold">Challenge.</span>
          </h1>

          <p
            className="font-serif-editorial text-2xl sm:text-3xl italic text-gold leading-snug"
            data-testid="signup-subheadline"
          >
            Pour back into yourself. Five days can change everything.
          </p>

          <p
            className="font-body text-base leading-relaxed text-espresso/80 max-w-[480px]"
            data-testid="signup-body"
          >
            Every day for 5 days you'll receive one powerful focus area, one
            reflection prompt, and one action that moves you closer to the woman
            you're becoming. Complete each day's check-in to unlock the next.
            It's free. It's yours. And it starts the moment you sign up.
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-6 pt-2"
            data-testid="signup-form"
          >
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs uppercase tracking-[0.24em] text-espresso/60">
                First Name
              </label>
              <input
                type="text"
                className="input-line"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                data-testid="input-first-name"
                autoComplete="given-name"
                required
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="font-body text-xs uppercase tracking-[0.24em] text-espresso/60">
                Email
              </label>
              <input
                type="email"
                className="input-line"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourbestlife.com"
                data-testid="input-email"
                autoComplete="email"
                required
              />
            </div>

            {error ? (
              <p
                className="font-body text-sm text-espresso/80 border-l-2 border-gold pl-3"
                data-testid="signup-error"
              >
                {error}
              </p>
            ) : null}

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="submit"
                className="btn-gold w-full sm:w-auto"
                disabled={loading}
                data-testid="signup-submit-btn"
              >
                {loading ? "Starting…" : "Start My Glow Up — It's Free →"}
              </button>
              <p
                className="font-body text-xs text-espresso/50 leading-relaxed"
                data-testid="signup-fineprint"
              >
                No spam. No subscription. Just 5 days of showing up for
                yourself.
                <br />
                All reflection answers are private — they are not shared or
                stored.
              </p>
            </div>
          </form>

          {/* Returning user — resume by email */}
          <div
            className="pt-6 border-t border-espresso/10"
            data-testid="returning-user-section"
          >
            {!showResume ? (
              <button
                type="button"
                onClick={() => setShowResume(true)}
                className="font-body text-sm text-espresso/70 hover:text-gold transition-colors duration-300 underline-offset-4 hover:underline"
                data-testid="resume-toggle-btn"
              >
                Already signed up? Continue with your email →
              </button>
            ) : (
              <form
                onSubmit={handleResume}
                className="flex flex-col gap-4"
                data-testid="resume-form"
              >
                <div className="flex flex-col gap-1">
                  <label className="font-body text-xs uppercase tracking-[0.24em] text-espresso/60">
                    Continue your challenge
                  </label>
                  <input
                    type="email"
                    className="input-line"
                    value={resumeEmail}
                    onChange={(e) => setResumeEmail(e.target.value)}
                    placeholder="The email you signed up with"
                    data-testid="input-resume-email"
                    autoComplete="email"
                    required
                  />
                </div>

                {resumeError ? (
                  <p
                    className="font-body text-sm text-espresso/80 border-l-2 border-gold pl-3"
                    data-testid="resume-error"
                  >
                    {resumeError}
                  </p>
                ) : null}

                <div className="flex items-center gap-4">
                  <button
                    type="submit"
                    className="btn-gold"
                    disabled={resumeLoading}
                    data-testid="resume-submit-btn"
                  >
                    {resumeLoading ? "Looking…" : "Continue →"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResume(false);
                      setResumeEmail("");
                      setResumeError("");
                    }}
                    className="font-body text-sm text-espresso/60 hover:text-espresso transition-colors"
                    data-testid="resume-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
